import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import Folder from "@mui/icons-material/Folder";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import {
  Chip,
  FormControlLabel,
  formControlLabelClasses,
  Radio,
  RadioGroup,
  Stack
} from "@mui/material";
import { Button, Spinner } from "react-bootstrap";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import { alpha, styled } from "@mui/material/styles";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import {
  TreeItem,
  treeItemClasses,
  TreeItemProps
} from "@mui/x-tree-view/TreeItem";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRepositoryObjects } from "../../backend/hooks/pmxActivity/queryGetRepositoryObjects";
import {
  ExtendedRepositoryObject,
  GetRepositoryObjectsInput,
  RepositoryObject,
  RepositoryObjectFolder,
  RepositoryTree
} from "../../backend/types";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";
import { BpCheckbox } from "../../helpers/treeHelper";
import FileIcon from "./fileIcon";
import { SVGIcons } from "./svgIcons/svgIcons";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";

const MAX_CHIPS = 30;

interface FileTreeViewProps {
  filesSelection: any;
  updateFileSelection: (fileType: FileType, node: any) => void;
  fileType: FileType;
  isFileHidden?: (file: ExtendedRepositoryObject) => boolean;
  treeSelection?: any; // only used for the IE because
  parentLoading?: boolean[];
}

export const StyledTreeItem = styled((props: TreeItemProps) => (
  <TreeItem {...props} />
))(({ theme }) => ({
  [`& .${treeItemClasses.iconContainer}`]: {
    "& .close": {
      opacity: 0.3
    }
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 9,
    paddingLeft: 5,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`
  },
  [`& .${treeItemClasses.content}`]: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 2
  },
  [`& .${formControlLabelClasses.root}`]: {
    margin: 0
  }
}));

export enum FileType {
  Input = "input",
  Script = "entryPoint",
  WorkingDir = "workingDir",
  CompareView = "compareView"
}

export function FileTreeView(props: FileTreeViewProps) {
  const { filesSelection, updateFileSelection, fileType, isFileHidden } = props;
  const { activityData } = useContext(PmxActivityDetailsContext);
  const [expanded, setExpanded] = useState<string[]>(["/"]);
  const [selected, setSelected] = useState<any>([]);
  const location = useLocation();

  const {
    files: repositoryObjects,
    foldersAndCount,
    callMultipleRepository,
    callRepositoryObjects,
    treeHierarchy,
    isGetRepositoryObjectsLoading
  } = useRepositoryObjects(activityData?.mainRepository?.id, false);

  useEffect(() => {
    if (
      activityData &&
      (!props.treeSelection ||
        (props.treeSelection.length === 0 &&
          !location.pathname.includes("IEPlugin")))
    ) {
      const repoIds = [
        ...(Array.isArray(activityData.readOnlyRepositories)
          ? activityData.readOnlyRepositories.map((repo: any) => repo.id)
          : []),
        activityData.mainRepository.id
      ];
      const repositoryObjectsInput: GetRepositoryObjectsInput[] = repoIds.map(
        (repoId: string) => ({
          repoId: String(repoId)
        })
      );
      callMultipleRepository(repositoryObjectsInput, false);
    }
  }, [activityData]);

  // request to get repository objects when folder name is clicked
  const onNodeSelectFunction = async (
    repositoryObject: ExtendedRepositoryObject,
    event?: any
  ) => {
    if (props.treeSelection.length > 0) return;

    event.stopPropagation();
    if (repositoryObject.absolutePath.endsWith("/")) {
      const { data } = await callRepositoryObjects({
        variables: {
          repoId: String(repositoryObject.repository.id),
          location: repositoryObject.absolutePath
        }
      });
      fileType === FileType.Input &&
        data &&
        getAllChildFiles(
          repositoryObject.repository.name,
          repositoryObject.absolutePath,
          data,
          true
        );

      const nameVar =
        repositoryObject.repository.name + "/" + repositoryObject.absolutePath;
      setExpanded((prev) =>
        prev.includes(nameVar) ? prev : [...prev, nameVar]
      );
    }
  };

  const clickShowMore = async (showMore: RepositoryObjectFolder) => {
    const { data } = await callRepositoryObjects({
      variables: {
        location: showMore.folderName,
        repoId: showMore.repository.id,
        pageNumber: showMore.currentPageNumber + 1
      }
    });

    if (
      fileType === FileType.Input &&
      data &&
      data.some((a2: RepositoryObject) =>
        selected.some(
          (s: any) =>
            s.absolutePath.startsWith(
              `${a2.repository.name}/${showMore.folderName}`
            ) && a2.repository.id === s.repositoryId
        )
      )
    ) {
      getAllChildFiles(
        showMore.repository.name,
        showMore.folderName,
        data,
        true
      );
    }
  };

  useEffect(() => {
    if (
      filesSelection &&
      filesSelection.length > 0 &&
      fileType === FileType.Input
    ) {
      setSelected(dedupeFiles(filesSelection));
    } else if (filesSelection) {
      setSelected(filesSelection);
    }
  }, []);

  const handleToggle = async (event: any, nodeIds: any) => {
    event.persist();
    if (typeof event.target.checked == "boolean") {
      setSelected(selected);
    } else {
      setExpanded(nodeIds);
      setSelected(selected);

      const iconClicked = event.target.closest(".MuiTreeItem-root");
      const labelClicked = iconClicked
        ?.querySelector("svg")
        ?.getAttribute("data-testid");

      // we load the content of the folder if the "plus" icon was clicked
      if (iconClicked && labelClicked === "AddBoxOutlinedIcon") {
        const absolutePath = iconClicked.getAttribute("data-absolutepath"); // TO-DO: Move these to constants
        const repoId = iconClicked.getAttribute("data-repoId");
        const repoName = iconClicked.getAttribute("data-repoName");

        if (absolutePath.endsWith("/")) {
          const { data } = await callRepositoryObjects({
            variables: {
              repoId: repoId,
              location: absolutePath
            }
          });

          if (
            Array.isArray(selected) &&
            selected.some(
              (item: any) =>
                item.absolutePath === `${repoName}/${absolutePath}` &&
                FileType === FileType
            )
          ) {
            getAllChildFiles(repoName, absolutePath, data, true);
          }
        }
      }
    }
  };

  const dedupeFiles = (allNodes: any[]) => {
    return allNodes.filter(
      (node, index, self) =>
        self.findIndex((n) => n.absolutePath === node.absolutePath) === index
    );
  };

  const getChildFiles = (repoName: string, directoryPath: string): any[] => {
    const tree = repositoryObjects
      .filter(
        (file) =>
          file.repository?.name === repoName &&
          `${file.repository?.name}/${file.name}`.includes(
            `${repoName}/${directoryPath}`
          )
      )
      .map((file) => ({
        repositoryId: file.repository.id,
        versionId: file.versionId ? file.versionId : "",
        absolutePath: file.repository.name + "/" + file.name
      }));

    return tree;
  };

  const getAllChildFiles = (
    repositoryName: string,
    absolutePath: string,
    data: RepositoryObject[],
    setState: boolean = false
  ): any[] => {
    const files = [
      ...(data
        ? data
            .filter(
              (file: RepositoryObject) =>
                file.repository?.name === repositoryName &&
                `${file.repository?.name}/${file.name}`.includes(
                  `${repositoryName}/${absolutePath}`
                )
            )
            .map((file: RepositoryObject) => ({
              repositoryId: file.repository.id,
              versionId: file.versionId ? file.versionId : "",
              absolutePath: file.repository.name + "/" + file.name
            }))
        : []),
      ...repositoryObjects
        .filter(
          (file: RepositoryObject) =>
            file.repository?.name === repositoryName &&
            `${file.repository?.name}/${file.name}`.includes(
              `${repositoryName}/${absolutePath}`
            )
        )
        .map((file: RepositoryObject) => ({
          repositoryId: file.repository.id,
          versionId: file.versionId ? file.versionId : "",
          absolutePath: file.repository.name + "/" + file.name
        }))
    ];

    if (setState) {
      const allNodes = selected.concat(files);
      setSelected(dedupeFiles(allNodes));
    }
    return files;
  };

  const onDeleteChip = (file: any) => {
    setSelected(
      selected.filter(
        (x: any) =>
          (x.repositoryId === file.repositoryId &&
            !x.absolutePath.includes(file.absolutePath)) ||
          x.repositoryId !== file.repositoryId
      )
    );
  };

  function getAllChildFilesForTree(repoName: string, path: string): any[] {
    let results: ExtendedRepositoryObject[] = [];

    function searchInRepository(repo: any) {
      if (repo.repository.name === repoName) {
        const pathParts = path.split("/").filter((part) => part);
        navigatePath(repo.repositoryObjects, pathParts, 0);
      }
    }

    function navigatePath(objects: any, pathParts: string[], index: number) {
      if (index >= pathParts.length) return; // navigated through all parts

      const currentPart = pathParts[index];

      objects.forEach((object: any) => {
        if (object.name === currentPart) {
          if (index === pathParts.length - 1) {
            // If we are at the last part, collect all files in this object
            collectFiles(object);
          } else {
            // If not at the last part, navigate deeper
            navigatePath(object.content || [], pathParts, index + 1);
          }
        }
      });
    }

    function collectFiles(object: any) {
      if (object.content) {
        object.content.forEach((item: any) => {
          if (item.absolutePath) {
            results.push(item);
          }
          if (item.content && item.content.length > 0) {
            collectFiles(item);
          }
        });
      }
    }

    props.treeSelection.forEach((repo: RepositoryObjectFolder) =>
      searchInRepository(repo)
    );

    return results.map((file) => ({
      repositoryId: file.repository.id,
      versionId: file.versionId ? file.versionId : "",
      absolutePath: file.repository.name + "/" + file.absolutePath
    }));
  }
  // checkbox selection
  const handleChangeEvent = async (node: any) => {
    let allNodes: any[] = selected;
    const nameVar = node.repository.name + "/" + node.absolutePath;
    let files = getChildFiles(node.repository.name, node.absolutePath);

    if (node.absolutePath.endsWith("/")) {
      if (props.treeSelection.length === 0) {
        const { data } = await callRepositoryObjects({
          variables: {
            repoId: String(node.repository.id),
            location: node.absolutePath
          }
        });
        files = [
          {
            repositoryId: node.repository.id,
            versionId: node.versionId ? node.versionId : "",
            absolutePath: node.repository.name + "/" + node.absolutePath
          },
          ...(data
            ? getAllChildFiles(node.repository.name, node.absolutePath, data)
            : [])
        ];
      } else {
        let treeFiles = getAllChildFilesForTree(
          node.repository.name,
          node.absolutePath
        );

        files = [
          ...treeFiles,
          {
            repositoryId: node.repository.id,
            versionId: node.versionId ? node.versionId : "",
            absolutePath: node.repository.name + "/" + node.absolutePath
          }
        ];
        if (
          selected.some((ai: any) =>
            files.some((a2) => a2.absolutePath === ai.absolutePath)
          )
        ) {
          // remove
          allNodes = selected.filter(
            (x: any) =>
              !files.some((x2: any) => x2.absolutePath === x.absolutePath)
          );
        } else {
          allNodes = allNodes.concat(files);
        }
      }

      setExpanded((prev) =>
        prev.includes(nameVar) ? prev : [...prev, nameVar]
      );

      if (
        selected.some((ai: any) =>
          files.some((a2) => a2.absolutePath === ai.absolutePath)
        )
      ) {
        // remove
        allNodes = selected.filter(
          (x: any) => !x.absolutePath.includes(nameVar)
        );
      } else {
        allNodes = allNodes.concat(files);
      }
    } else {
      // for files selection
      if (
        Array.isArray(selected) &&
        selected.some((ai: any) => ai.absolutePath.includes(nameVar))
      ) {
        allNodes = selected.filter(
          (x: any) => !x.absolutePath.includes(nameVar)
        );
      } else {
        const newNode = {
          repositoryId: node.repository.id,
          versionId: node.versionId ? node.versionId : "",
          absolutePath: nameVar
        };
        if (
          !allNodes.some((x: any) => x.absolutePath === newNode.absolutePath)
        ) {
          allNodes = allNodes.concat(newNode);
        }
      }
    }

    setSelected(dedupeFiles(allNodes));
    updateFileSelection(fileType, allNodes);
  };

  const handleRadioChangeEvent = (node: ExtendedRepositoryObject) => {
    if (fileType === FileType.Script || fileType === FileType.CompareView) {
      if (!node.absolutePath.endsWith("/")) {
        updateFileSelection(fileType, {
          repositoryId: node.repository.id,
          versionId: node.versionId,
          absolutePath: `${node.repository.name}/${node.absolutePath}`
        });
        setSelected(`${node.repository.name}/${node.absolutePath}`);
      }
    } else if (fileType === FileType.WorkingDir) {
      if (typeof node == "string") {
        updateFileSelection(fileType, `${node}/`);
        setSelected(`${node}/`);
      } else {
        updateFileSelection(
          fileType,
          `${node.repository.name}/${node.absolutePath}`
        );
        setSelected(`${node.repository.name}/${node.absolutePath}`);
      }
    }
  };

  const onScrollDownClick = () => {
    const modalContent = document.getElementById("modal-content");
    if (modalContent && modalContent.parentElement) {
      modalContent.parentElement.scrollTo({
        top: modalContent.parentElement.scrollHeight,
        behavior: "smooth"
      });
    }
  };

  const isHidden = (file: ExtendedRepositoryObject) => {
    return isFileHidden ? isFileHidden(file) : !file.absolutePath.endsWith("/");
  };

  const renderTreeNodes = (
    data: ExtendedRepositoryObject[],
    repoId: string = ""
  ) =>
    data.map((item: ExtendedRepositoryObject) => {
      const showMore: RepositoryObjectFolder | undefined =
        props.treeSelection.length === 0 && item.absolutePath.endsWith("/")
          ? foldersAndCount.find(
              (folder) =>
                folder.folderName === item.absolutePath &&
                item.content &&
                folder.count > item.content.length &&
                folder.repository.id === item.repository.id
            )
          : undefined;

      const isFolder = item.absolutePath.endsWith("/");
      // how the label will look like for both file and folder,
      // the checkbox/radio will be added based on the file type
      const label = isFolder ? (
        <div
          onClick={(event) => {
            onNodeSelectFunction(item, event);
          }}
        >
          {expanded.includes(
            `${item.repository?.name}/${item.absolutePath}`
          ) ? (
            <SVGIcons key={item.absolutePath} svgName="folder-open" />
          ) : (
            <Folder key={item.absolutePath} className="folder_icon" />
          )}
          {item.name}
        </div>
      ) : (
        <div
          data-revision={item.revision}
          data-versionid={item.versionId}
          data-absolutepath={item.absolutePath}
          onClick={(e) => e.stopPropagation()}
          style={{ userSelect: "none" }}
        >
          <FileIcon
            fileName={item.name}
            overallValidityStatus={item.validity?.overallValidityStatus}
            fontSize={"small"}
            absolutePath={item.absolutePath}
          />
          {item.name}
        </div>
      );

      if (item.content && item.content.length > 0) {
        return (
          <StyledTreeItem
            key={item.absolutePath}
            itemId={`${item.repository.name}/${item.absolutePath}`}
            data-testid={"treeItem_" + item.absolutePath}
            data-absolutepath={item.absolutePath}
            data-repoId={item.repository.id}
            data-repoName={item.repository.name}
            label={
              fileType === FileType.Input ? ( // input and it's folder checkbox
                <FormGroup>
                  <FormControlLabel
                    control={
                      <BpCheckbox
                        checked={selected.some(
                          (x: any) =>
                            x.absolutePath ===
                            `${item.repository?.name}/${item.absolutePath}`
                        )}
                        onChange={() => handleChangeEvent(item)}
                      />
                    }
                    label={label}
                  />
                </FormGroup>
              ) : (
                <FormControl>
                  <RadioGroup>
                    <FormControlLabel
                      value={`${item.repository?.name}/${item.absolutePath}`}
                      control={
                        <Radio
                          disableRipple
                          className="p-1"
                          onChange={() => handleRadioChangeEvent(item)}
                          size="small"
                          checked={
                            selected ===
                            `${item.repository?.name}/${item.absolutePath}`
                          }
                          value={`${item.repository?.name}/${item.absolutePath}`}
                          hidden={isHidden(item)}
                        />
                      }
                      label={label}
                    />
                  </RadioGroup>
                </FormControl>
              )
            }
          >
            {renderTreeNodes(item.content, repoId)}
            {showMore && (
              <a
                className="btn text-primary d-flex ms-5"
                onClick={() => clickShowMore(showMore)}
              >
                Show {showMore.count - item.content.length} more...
              </a>
            )}
          </StyledTreeItem>
        );
      }

      return (fileType === FileType.WorkingDir &&
        item.absolutePath.endsWith("/")) ||
        fileType !== FileType.WorkingDir ? (
        <StyledTreeItem
          key={item.absolutePath}
          itemId={`${item.repository.name}/${item.absolutePath}`}
          data-absolutepath={item.absolutePath}
          data-repoId={item.repository.id}
          data-repoName={item.repository.name}
          label={
            fileType === FileType.Input ? ( // input and it's file
              <div>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <BpCheckbox
                        checked={selected.some(
                          (x: any) =>
                            x.absolutePath ===
                            `${item.repository.name}/${item.absolutePath}`
                        )}
                        onChange={() => handleChangeEvent(item)}
                      />
                    }
                    label={label}
                  />
                </FormGroup>
              </div>
            ) : fileType === FileType.Script &&
              !item.absolutePath.endsWith("/") ? (
              <FormControl>
                <RadioGroup>
                  <FormControlLabel
                    value={`${item.repository.name}/${item.absolutePath}`}
                    control={
                      <Radio
                        onChange={() => handleRadioChangeEvent(item)}
                        size="small"
                        className="p-1 ms-4"
                        disableRipple
                        checked={
                          selected ===
                          `${item.repository.name}/${item.absolutePath}`
                        }
                        value={`${item.repository.name}/${item.absolutePath}`}
                        hidden={isHidden(item)}
                      />
                    }
                    label={label}
                  />
                </RadioGroup>
              </FormControl>
            ) : fileType !== FileType.Script ? (
              <FormControl>
                <RadioGroup>
                  <FormControlLabel
                    value={`${item.repository.name}/${item.absolutePath}`}
                    control={
                      <Radio
                        onChange={() => handleRadioChangeEvent(item)}
                        size="small"
                        className="p-1 ms-4"
                        disableRipple
                        checked={
                          selected ===
                          `${item.repository.name}/${item.absolutePath}`
                        }
                        value={`${item.repository.name}/${item.absolutePath}`}
                        hidden={isHidden(item)}
                      />
                    }
                    label={label}
                  />
                </RadioGroup>
              </FormControl>
            ) : (
              label
            )
          }
        >
          {item.absolutePath.endsWith("/") && (
            <StyledTreeItem
              key={`inner_${item.repository.name}/${item.absolutePath}`}
              itemId={`inner_${item.repository.name}/${item.absolutePath}`}
              label={<></>}
            />
          )}
        </StyledTreeItem>
      ) : (
        <></>
      );
    });

  const currentTree =
    props.treeSelection && props.treeSelection.length > 0
      ? props.treeSelection
      : treeHierarchy;

  return (
    <div>
      {(!currentTree || currentTree.length === 0) && // only show loading on initial load, as for the rest it becomes annoying
        (isGetRepositoryObjectsLoading || props.parentLoading) && (
          <div className="center_div">
            <Spinner animation="border" className="spinner_color" />
            <p>Loading...</p>
          </div>
        )}
      {currentTree?.map((tree: RepositoryTree) => {
        return (
          <SimpleTreeView
            key={`${tree.repository.name}-tree`}
            className="mt-2 tree-view text-break"
            slots={{
              expandIcon: AddBoxOutlinedIcon,
              collapseIcon: IndeterminateCheckBoxOutlinedIcon
            }}
            onExpandedItemsChange={handleToggle}
            defaultExpandedItems={["/"]}
            expandedItems={expanded}
            selectedItems={selected}
          >
            <StyledTreeItem
              key={"/"}
              itemId={"/"}
              data-absolutepath={"/"}
              data-repoId={tree.repository.id}
              data-repoName={tree.repository.name}
              label={
                <>
                  {fileType === FileType.WorkingDir ? (
                    <RadioGroup>
                      <FormControlLabel
                        value={`${tree.repository.name}/`}
                        control={
                          <Radio
                            disableRipple
                            className="p-1"
                            onChange={(event) =>
                              handleRadioChangeEvent(event.target.value)
                            }
                            size="small"
                            checked={selected === `${tree.repository.name}/`}
                            value={tree.repository.name}
                          />
                        }
                        label={tree.repository.name}
                      />
                    </RadioGroup>
                  ) : (
                    <>
                      <CenterFocusStrongIcon className="grey_color" />
                      {tree.repository.name}
                    </>
                  )}
                </>
              }
            >
              {renderTreeNodes(tree.repositoryObjects, tree.repository.id)}
            </StyledTreeItem>
          </SimpleTreeView>
        );
      })}
      {Array.isArray(selected) && selected.length > 0 && (
        <>
          <Button
            variant="light"
            onClick={onScrollDownClick}
            className="position-absolute top-0 end-0 mt-3 me-3"
          >
            <ArrowCircleDownIcon /> Go Down
          </Button>
          <Stack direction="row" flexWrap="wrap" spacing={1} className="mt-3">
            {selected.map((file: any, index: number) => {
              if (index < MAX_CHIPS) {
                return (
                  <Chip
                    key={file.absolutePath + "_chip"}
                    label={file.absolutePath}
                    onDelete={() => {
                      onDeleteChip(file);
                    }}
                    className="m-1"
                  />
                );
              }
              return null;
            })}
            {selected.length > MAX_CHIPS && (
              <Chip
                key={"more_files_chip"}
                label={`and at least ${selected.length - MAX_CHIPS} more...`}
              />
            )}
          </Stack>
        </>
      )}
    </div>
  );
}
